/**
 * useAnalytics Hook
 *
 * Initializes PostHog SDK after user consent
 * Handles analytics lifecycle
 *
 * User Story: US2 - Consent Management
 */

import { useEffect, useState, useCallback } from 'react';
import posthog from 'posthog-js';

/**
 * useAnalytics Hook
 *
 * Initializes analytics SDK after consent
 *
 * @param consent - Whether user has given consent (null = undecided)
 * @param userId - Optional: user ID for authenticated users
 * @returns Analytics functions and status
 */
export function useAnalytics(consent: boolean | null, userId?: string) {
  const [isInitialized, setIsInitialized] = useState(false);

  /**
   * Track event in PostHog
   */
  const trackEvent = useCallback((eventName: string, properties?: Record<string, unknown>) => {
    try {
      if (posthog && typeof posthog.capture === 'function') {
        posthog.capture(eventName, properties);
      }
    } catch (err) {
      // Fail silently
    }
  }, []);

  /**
   * Identify user in PostHog
   */
  const identify = useCallback((id: string, properties?: Record<string, unknown>) => {
    try {
      if (posthog && typeof posthog.identify === 'function') {
        posthog.identify(id, properties);
      }
    } catch (err) {
      // Fail silently
    }
  }, []);

  /**
   * Reset analytics when user logs out
   */
  const reset = useCallback(() => {
    try {
      if (posthog && typeof posthog.reset === 'function') {
        posthog.reset();
      }
    } catch (err) {
      // Fail silently
    }
  }, []);

  /**
   * Initialize analytics on consent change
   */
  useEffect(() => {
    setIsInitialized(consent === true);
    if (consent === true && userId) {
      identify(userId);
    } else if (consent === false) {
      reset();
    }
  }, [consent, userId, identify, reset]);

  return {
    isInitialized,
    trackEvent,
    identify,
    reset,
  };
}

export default useAnalytics;
